/**
 * <t82-auth-section> — Self-contained auth UI (email OTP login/logout).
 *
 * Handles all auth states internally: idle → email → code → logged-in.
 * Uses httpOnly cookies for session persistence (set by server).
 * Cross-tab sync via BroadcastChannel.
 */

import { html } from 'lit';
import { T82Element } from '../../components/t82-element.ts';
import { showMsg, escapeHtml } from '../../utils';

type AuthMode = 'idle' | 'email' | 'code' | 'loggedIn';

const AUTH_CHANNEL = 't82_auth';

export class T82AuthSection extends T82Element {
  static properties = {
    mode: { type: String, attribute: false },
    loading: { type: Boolean, attribute: false },
    email: { type: String, attribute: false },
    pendingEmail: { type: String, attribute: false },
  };

  declare mode: AuthMode;
  declare loading: boolean;
  declare email: string;
  declare pendingEmail: string;

  userId: string | null = null;

  private _bc: BroadcastChannel | null = null;
  private _onClickOutside = (e: MouseEvent) => {
    if (this.mode !== 'email' && this.mode !== 'code') return;
    const target = e.target as Node;
    // Ignore clicks on elements removed by Lit re-render (e.g. the old Login button)
    if (!document.contains(target)) return;
    if (this.contains(target)) return;
    this._cancel();
  };

  constructor() {
    super();
    this.mode = 'idle';
    this.loading = false;
    this.email = '';
    this.pendingEmail = '';
  }

  override connectedCallback() {
    super.connectedCallback();
    this._checkSession();
    this._bc = new BroadcastChannel(AUTH_CHANNEL);
    this._bc.onmessage = (e) => this._onBroadcast(e.data);
    document.addEventListener('click', this._onClickOutside);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this._bc?.close();
    this._bc = null;
    document.removeEventListener('click', this._onClickOutside);
  }

  override render() {
    switch (this.mode) {
      case 'loggedIn':
        return html`
          <span class="auth-user" title="${escapeHtml(this.email)}">${escapeHtml(this.email)}</span>
          <button class="nav-link" @click=${this._doLogout}>Logout</button>
        `;

      case 'email':
        return html`
          <div class="auth-dropdown">
            <button class="nav-link active">Login</button>
            <div class="auth-dropdown-menu">
              <label class="auth-label">Email</label>
              <input class="auth-email-input" type="email" id="authEmailInput"
                placeholder="email@example.com" ?disabled=${this.loading}
                @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._doSendCode(); }}>
              <button class="auth-submit" @click=${this._doSendCode}
                ?disabled=${this.loading}>Send Code</button>
            </div>
          </div>
        `;

      case 'code':
        return html`
          <div class="auth-dropdown">
            <button class="nav-link active">Login</button>
            <div class="auth-dropdown-menu">
              <span class="auth-status">Code sent to ${escapeHtml(this.pendingEmail)}</span>
              <input class="auth-code-input" type="text" id="authCodeInput"
                maxlength="6" placeholder="000000" ?disabled=${this.loading}
                @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this._doVerify(); }}>
              <button class="auth-submit" @click=${this._doVerify}
                ?disabled=${this.loading}>Verify</button>
            </div>
          </div>
        `;

      default:
        return html`
          <button class="nav-link" @click=${this._showEmailInput}>Login</button>
        `;
    }
  }

  override updated(changed: Map<string, unknown>) {
    // Auto-focus input fields after render
    if (changed.has('mode')) {
      if (this.mode === 'email') {
        setTimeout(() => this.querySelector<HTMLInputElement>('#authEmailInput')?.focus(), 50);
      } else if (this.mode === 'code') {
        setTimeout(() => this.querySelector<HTMLInputElement>('#authCodeInput')?.focus(), 50);
      }
    }
  }

  /** Check session on page load via /api/themes/auth/me */
  private async _checkSession() {
    try {
      const resp = await fetch('/api/themes/auth/me', { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        this.email = data.email;
        this.userId = data.userId;
        this.mode = 'loggedIn';
      }
      // 401 or error → stay idle (no cookie or expired)
    } catch {
      // Network error → stay idle
    }
  }

  private _onBroadcast(data: { type: string; email?: string; userId?: string }) {
    if (data.type === 'login' && data.email) {
      this.email = data.email;
      this.userId = data.userId ?? null;
      this.mode = 'loggedIn';
    } else if (data.type === 'logout') {
      this.email = '';
      this.userId = null;
      this.pendingEmail = '';
      this.mode = 'idle';
    }
  }

  private _showEmailInput() {
    this.mode = 'email';
  }

  private _cancel() {
    this.pendingEmail = '';
    this.mode = 'idle';
  }

  private async _doSendCode() {
    const input = this.querySelector<HTMLInputElement>('#authEmailInput');
    const email = input?.value.trim().toLowerCase() || '';
    if (!email || !email.includes('@')) {
      showMsg('Enter a valid email address', 'warn');
      return;
    }

    this.loading = true;
    try {
      const resp = await fetch('/api/themes/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        this.pendingEmail = email;
        this.mode = 'code';
        showMsg('Verification code sent to ' + email, 'info');
      } else {
        showMsg(data.error || 'Failed to send code', 'warn');
      }
    } catch (e) {
      showMsg('Network error: ' + (e as Error).message, 'warn');
    } finally {
      this.loading = false;
    }
  }

  private async _doVerify() {
    const input = this.querySelector<HTMLInputElement>('#authCodeInput');
    const code = input?.value.trim() || '';
    if (!code || code.length !== 6) {
      showMsg('Enter the 6-digit code', 'warn');
      return;
    }

    this.loading = true;
    try {
      const resp = await fetch('/api/themes/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: this.pendingEmail, code }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        this.email = data.email;
        this.userId = data.userId;
        this.pendingEmail = '';
        this.mode = 'loggedIn';
        this._bc?.postMessage({ type: 'login', email: data.email, userId: data.userId });
        showMsg('Logged in as ' + data.email, 'info');
      } else {
        showMsg(data.error || 'Verification failed', 'warn');
      }
    } catch (e) {
      showMsg('Network error: ' + (e as Error).message, 'warn');
    } finally {
      this.loading = false;
    }
  }

  private async _doLogout() {
    try {
      await fetch('/api/themes/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — clear local state regardless
    }
    this.email = '';
    this.userId = null;
    this.pendingEmail = '';
    this.mode = 'idle';
    this._bc?.postMessage({ type: 'logout' });
    showMsg('Logged out', 'info');
  }
}

customElements.define('t82-auth-section', T82AuthSection);

declare global {
  interface HTMLElementTagNameMap {
    't82-auth-section': T82AuthSection;
  }
}
